import ajax from '@/helpers/ajax'
import { htmlLang } from '@/i18n/translations'
import { importWithRetry } from '@/utils/component-loader'

let service = null

if (window.Agra.Configuration.stubGoogleMaps) {
  // In scenarios, we mock out Google Maps calls with our own stub data.
  service = {
    stubbedService: null,

    async loadStubbedService() {
      const module = await importWithRetry(() => import(/* webpackMode: "lazy" */ './stubbed-maps-service'))
      this.stubbedService = module.default
    },

    autocomplete(params) {
      return this.loadStubbedService().then(() => {
        return this.stubbedService.autocomplete(params)
      })
    },

    geocode(params) {
      return this.loadStubbedService().then(() => {
        return this.stubbedService.geocode(params)
      })
    },

    findPlace(params) {
      return this.loadStubbedService().then(() => {
        return this.stubbedService.findPlace(params)
      })
    },

    getPlaceDetails(params) {
      return this.loadStubbedService().then(() => {
        return this.stubbedService.getPlaceDetails(params)
      })
    }
  }
} else {
  const MapsService = {
    autocomplete({ input, bounds, countries }) {
      const tokyoHost = window.Agra.Configuration.tokyoUrlBase

      const center = window.Agra.Configuration.locationSearchBias.center
      const urlParams = new URLSearchParams({
        query: input,
        bounds: bounds ? JSON.stringify(bounds) : '',
        center: center ? JSON.stringify(center) : '',
        countries: countries ? JSON.stringify(countries) : '',
        locale: htmlLang()
      })

      const url = `${tokyoHost}/maps/places/autocomplete?${urlParams.toString()}`

      // Return a promise; callers should use it with .then to get the data
      return new Promise((resolve, reject) => {
        ajax.get({
          url,
          camelCaseResponse: false,
          successHandler: (resp) => {
            resolve(resp)
          },
          errorHandler: (err) => {
            reject(err)
          }
        })
      })
    },

    geocode({ address, bounds, country }) {
      const tokyoHost = window.Agra.Configuration.tokyoUrlBase

      const urlParams = new URLSearchParams({
        query: address,
        bounds: bounds ? JSON.stringify(bounds) : '',
        country: country || '',
        locale: htmlLang()
      })

      const url = `${tokyoHost}/maps/geocode?${urlParams.toString()}`

      // Return a promise; callers should use it with .then to get the data
      return new Promise((resolve, reject) => {
        ajax.get({
          url,
          camelCaseResponse: false,
          successHandler: (resp) => {
            resolve(resp)
          },
          errorHandler: (err) => {
            reject(err)
          }
        })
      })
    },

    findPlace({ query, bounds }) {
      const tokyoHost = window.Agra.Configuration.tokyoUrlBase

      const center = window.Agra.Configuration.locationSearchBias.center
      const urlParams = new URLSearchParams({
        query,
        bounds: bounds ? JSON.stringify(bounds) : '',
        center: center ? JSON.stringify(center) : '',
        locale: htmlLang()
      })

      const url = `${tokyoHost}/maps/places/search?${urlParams.toString()}`

      // Return a promise; callers should use it with .then to get the data
      return new Promise((resolve, reject) => {
        ajax.get({
          url,
          camelCaseResponse: false,
          successHandler: (resp) => {
            resolve(resp)
          },
          errorHandler: (err) => {
            reject(err)
          }
        })
      })
    },

    getPlaceDetails({ placeId }) {
      const tokyoHost = window.Agra.Configuration.tokyoUrlBase
      const encodedPlaceId = encodeURIComponent(placeId)

      const urlParams = new URLSearchParams({
        locale: htmlLang()
      })

      const url = `${tokyoHost}/maps/places/${encodedPlaceId}/details?${urlParams.toString()}`

      // Return a promise; callers should use it with .then to get the data
      return new Promise((resolve, reject) => {
        ajax.get({
          url,
          camelCaseResponse: false,
          successHandler: (resp) => {
            resolve(resp)
          },
          errorHandler: (err) => {
            reject(err)
          }
        })
      })
    }
  }

  service = MapsService
}

export default service
